import { useEffect, useState } from "react";
import { Receipt, Loader2, Save, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";
const Label = ({ children }) => <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">{children}</span>;
const FIELDS = [
  ["business_name", "Business name", "Logerd"],
  ["email", "Billing / support email", "billing@yourcompany.com"],
  ["phone", "Phone", "+1 (555) 000-0000"],
  ["tax_id", "Tax ID / EIN (optional)", ""],
  ["logo_url", "Logo URL (optional — defaults to the Logerd logo)", "https://…/logo.png"],
];

export function ReceiptProfileCard() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/platform/receipt-profile").then(({ data }) => setP(data)).catch(() => setP({}));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/platform/receipt-profile", p);
      setP(data);
      toast.success("Receipt branding saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const preview = () => {
    const token = localStorage.getItem("vlog_token");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/platform/receipt-profile/preview`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => { const w = window.open("", "_blank"); w.document.write(html); w.document.close(); })
      .catch(() => toast.error("Preview failed"));
  };

  if (!p) return null;
  return (
    <form onSubmit={save} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm mt-6" data-testid="receipt-profile-card">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="rounded-lg bg-primary/10 p-2 text-primary"><Receipt className="h-4 w-4" /></span>
          <div>
            <h2 className="font-display text-base font-semibold text-slate-900">Receipt branding</h2>
            <p className="text-xs text-slate-500">Your logo and business address printed on every receipt and failed-payment email sent to clients.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={preview} data-testid="receipt-preview-btn" className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Eye className="h-3.5 w-3.5" /> Preview</button>
          <button type="submit" disabled={saving} data-testid="receipt-save-btn" className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-xs font-semibold text-white disabled:opacity-60">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save</button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label, ph]) => (
          <label key={k} className={`block ${k === "logo_url" ? "sm:col-span-2" : ""}`}>
            <Label>{label}</Label>
            <input value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} placeholder={ph} className={inputCls} data-testid={`receipt-${k}-input`} />
          </label>
        ))}
        <label className="block sm:col-span-2">
          <Label>Business address</Label>
          <textarea value={p.address || ""} onChange={(e) => setP({ ...p, address: e.target.value })} rows={3} placeholder={"123 Main St, Suite 100\nAtlanta, GA 30303"} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" data-testid="receipt-address-input" />
        </label>
      </div>
    </form>
  );
}
